import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Text, Button, Divider } from "@mantine/core";
import { IconCameraOff, IconId, IconUser } from "@tabler/icons-react";
import { trackEvent } from "../utils/telemetry";
import { isChromeFrozenUA } from "../utils/androidDetection";

// sessionStorage key used to remember that the Android block was shown in
// this tab. The refresh button triggers window.location.reload() which
// wipes React state, so a storage marker is the only way to correlate
// "block shown" with "camera later started working" in the same session.
const ANDROID_BLOCK_STORAGE_KEY = "veratad_android_camera_os_block";

/**
 * Marks the sessionStorage flag so a subsequent successful camera init
 * (typically after window.location.reload()) can fire the
 * `android_camera_os_block_recovered` event with timing info.
 */
function markAndroidBlockShown(meta) {
  try {
    sessionStorage.setItem(
      ANDROID_BLOCK_STORAGE_KEY,
      JSON.stringify({ shown_at: Date.now(), ...meta })
    );
  } catch (e) {
    // sessionStorage disabled / private mode — funnel will just miss this user
  }
}

/**
 * Returns the parsed Android block marker if present, otherwise null.
 * Does not modify sessionStorage. Used to detect "user came back after
 * refresh" without consuming the marker.
 */
export function getPendingAndroidBlock() {
  let stored;
  try {
    stored = sessionStorage.getItem(ANDROID_BLOCK_STORAGE_KEY);
  } catch (e) {
    return null;
  }
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch (e) {
    return null;
  }
}

/**
 * Fires `android_camera_os_block_returned` when the marker is present
 * on page load — signals the user came back to the page after tapping
 * refresh. Separates "didn't come back" drop-off from "came back but
 * camera still blocked" drop-off in the funnel.
 *
 * Does NOT clear the marker — that stays intact so
 * `fireAndroidBlockRecoveredIfPending` can still fire when camera works.
 * Uses a module-level ref to prevent double-firing within the same tab.
 */
let _returnedFired = false;
export function fireAndroidBlockReturnedIfPending({
  requestId,
  captureType,
  isBrowserHandoffSession = false,
} = {}) {
  if (_returnedFired) return;
  const meta = getPendingAndroidBlock();
  if (!meta) return;
  _returnedFired = true;

  trackEvent("android_camera_os_block_returned", {
    capture_type: captureType,
    is_browser_handoff: isBrowserHandoffSession,
    android_variant: meta.android_variant,
    is_chrome_frozen_ua: meta.is_chrome_frozen_ua,
    time_since_shown_ms: Date.now() - (meta.shown_at || Date.now()),
  });
}

/**
 * If the user previously saw the Android block portal in this tab and
 * camera has now started working, fires the `android_camera_os_block_recovered`
 * event and clears the marker. Call this from wherever you detect camera
 * readiness (e.g. handleCaptureReady in the parent page component).
 *
 * Fires on every call until the marker is cleared (guarded by removeItem)
 * so it's safe to wire from multiple code paths without double-firing.
 */
export function fireAndroidBlockRecoveredIfPending({
  requestId,
  captureType,
  isBrowserHandoffSession = false,
} = {}) {
  let stored;
  try {
    stored = sessionStorage.getItem(ANDROID_BLOCK_STORAGE_KEY);
  } catch (e) {
    return;
  }
  if (!stored) return;

  let meta;
  try {
    meta = JSON.parse(stored);
  } catch (e) {
    try {
      sessionStorage.removeItem(ANDROID_BLOCK_STORAGE_KEY);
    } catch (_) {
      /* sessionStorage unavailable */
    }
    return;
  }

  try {
    sessionStorage.removeItem(ANDROID_BLOCK_STORAGE_KEY);
  } catch (e) {
    /* sessionStorage unavailable */
  }

  trackEvent("android_camera_os_block_recovered", {
    capture_type: captureType,
    is_browser_handoff: isBrowserHandoffSession,
    android_variant: meta.android_variant,
    is_chrome_frozen_ua: meta.is_chrome_frozen_ua,
    time_to_recover_ms: Date.now() - (meta.shown_at || Date.now()),
  });
}

/**
 * Full-screen portal shown when camera access is blocked on an Android
 * device. Two variants:
 *
 *   "os_toggle_12plus"         — Android 12+, walks the user through the
 *                                 Quick Settings Camera access toggle.
 *   "chrome_permission_legacy" — Android 10/11 (no OS toggle available),
 *                                 walks the user through Chrome's
 *                                 address-bar site permissions.
 *
 * Fires `android_camera_os_block_shown` on mount and
 * `android_camera_os_block_refresh_clicked` when the retry button is tapped.
 * Both events include `android_variant` so the funnel can be segmented.
 * A sessionStorage marker is also written so the parent page can fire
 * `android_camera_os_block_recovered` when camera later starts working.
 */
export default function AndroidCameraBlockPortal({
  locale,
  requestId,
  captureType, // "id_doc" | "face"
  isBrowserHandoffSession = false,
  androidVariant = "os_toggle_12plus",
  onPhotoFallback,
  captureMode = "environment", // "environment" (back camera, ID docs) | "user" (front camera, selfie)
}) {
  const shownRef = useRef(false);
  const fileInputRef = useRef(null);

  const baseProps = {
    capture_type: captureType,
    is_browser_handoff: isBrowserHandoffSession,
    android_variant: androidVariant,
    is_chrome_frozen_ua: isChromeFrozenUA,
  };

  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    trackEvent("android_camera_os_block_shown", baseProps);
    // Persist the marker so the parent can fire _recovered on the next
    // successful camera init (typically after window.location.reload()).
    markAndroidBlockShown({
      android_variant: androidVariant,
      is_chrome_frozen_ua: isChromeFrozenUA,
      capture_type: captureType,
    });
  }, []);

  const handleRefresh = () => {
    trackEvent("android_camera_os_block_refresh_clicked", baseProps);
    window.location.reload();
  };

  const handleFallbackClick = () => {
    trackEvent("android_camera_os_block_fallback_clicked", baseProps);
    // Programmatically trigger the hidden <input type="file"> with
    // capture="environment" — Android Chrome launches the system
    // camera app directly, bypassing Chrome's site permission cache
    // and the getUserMedia permission model entirely.
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-selected if the
    // parent flow somehow returns to this portal.
    e.target.value = "";
    if (!file) return;
    trackEvent("android_camera_os_block_fallback_captured", {
      ...baseProps,
      file_size_bytes: file.size,
      file_type: file.type,
    });
    if (typeof onPhotoFallback === "function") {
      onPhotoFallback(file);
    }
  };

  // Android 10/11: no Quick Settings Camera toggle. Steps walk through
  // Chrome's address-bar site permissions instead. Title, message, and
  // primary CTA are the same for both variants — only the numbered steps
  // and refresh button label change.
  const isLegacy = androidVariant === "chrome_permission_legacy";
  const isGeneric = androidVariant === "generic";
  const isFace = captureType === "face";

  // Unified title — camera-app fallback works regardless of variant, so
  // the headline pivots straight to the solution instead of dwelling on
  // the problem.
  const title = locale?.errors?.androidCameraBlockTitle || "Camera not available";

  // Unified subtitle — tells the user there's an easy alternative.
  const message =
    locale?.errors?.androidCameraBlockMessage ||
    "Take a photo using your phone's camera app to continue.";

  // Numbered steps for the secondary (settings) path — these differ
  // between Android 12+ Quick Settings and Android 10/11 Chrome perms.
  const step1 = isLegacy
    ? locale?.errors?.cameraBlockedStep1 ||
      "1. Tap the lock icon in your browser's address bar"
    : locale?.errors?.androidCameraBlockStep1 ||
      "1. Swipe down from the top of your screen to open Quick Settings";

  const step2 = isLegacy
    ? locale?.errors?.cameraBlockedStep2 || "2. Find Camera permissions"
    : locale?.errors?.androidCameraBlockStep2 ||
      "2. Look for the \"Camera access\" tile";

  const step3 = isLegacy
    ? locale?.errors?.cameraBlockedStep3 || "3. Change to \"Allow\""
    : locale?.errors?.androidCameraBlockStep3 ||
      "3. Tap it to switch from Blocked to Available";

  const step4 = isLegacy
    ? locale?.errors?.cameraBlockedStep4 ||
      "4. Come back and tap the button below"
    : locale?.errors?.androidCameraBlockStep4 ||
      "4. Come back here and tap the button below";

  const refreshButtonLabel = isLegacy
    ? locale?.errors?.tryAgainAfterUnblock || "Try Again"
    : locale?.errors?.androidCameraBlockRefresh || "Refresh & Try Again";

  // Camera-app fallback button label + icon depends on what we're
  // capturing — ID card icon + "Take photo of your ID" for document
  // capture, user icon + "Take a selfie" for face capture.
  const fallbackButtonLabel = isFace
    ? locale?.errors?.androidCameraBlockSelfieFallbackButton || "Take a selfie"
    : locale?.errors?.androidCameraBlockFallbackButton || "Take photo of your ID";

  const fallbackButtonIcon = isFace ? (
    <IconUser size={22} stroke={2} />
  ) : (
    <IconId size={22} stroke={2} />
  );

  const portal = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 9999,
        backgroundColor: "#0a0a0a",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: "48px 24px 32px",
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    >
      {/* Header: icon + title + one-line subtitle that points to the solution */}
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          backgroundColor: "rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 20,
        }}
      >
        <IconCameraOff size={36} color="rgba(255,255,255,0.85)" stroke={1.5} />
      </div>
      <Text c="white" fz={24} fw={700} align="center" mb={8}>
        {title}
      </Text>
      <Text c="rgba(255,255,255,0.65)" fz={15} align="center" maw={320} mb={32}>
        {message}
      </Text>

      {/* PRIMARY CTA — camera-app fallback. Full-width (capped) button with
          an icon that tells the user exactly what they're photographing. */}
      {typeof onPhotoFallback === "function" && (
        <>
          {/* Plain filled CTA — same as every other primary button in the
              app. It picks up the customer's primaryColor/primaryShade as its
              background and buttonTextColor as its label via the global Button
              theme in App.js, so contrast tracks the customer palette on the
              portal's dark backdrop. It deliberately does NOT use
              variant="white"/color="dark": that path resolved the label color
              from the theme's `dark` palette, which App.js overrides with the
              customer's (often light) colorMuted, producing an invisible
              label. */}
          <Button
            size="lg"
            fullWidth
            leftIcon={fallbackButtonIcon}
            onClick={handleFallbackClick}
            styles={{
              root: {
                maxWidth: 340,
                height: 56,
                fontSize: 16,
                fontWeight: 600,
              },
              label: {
                whiteSpace: "normal",
              },
            }}
          >
            {fallbackButtonLabel}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture={captureMode}
            style={{ display: "none" }}
            onChange={handleFileSelected}
          />
        </>
      )}

      {/* SECONDARY — Android-specific unblock instructions.
          Only rendered for Android variants. Non-Android users
          (the "generic" variant) skip straight from the primary
          camera-app button to the refresh button below. */}
      {!isGeneric && (
        <>
          {/* Visual divider — clearly separates the primary CTA from the
              secondary "fix settings" path so users aren't confused
              about which is the recommended action. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              maxWidth: 340,
              marginTop: 40,
              marginBottom: 20,
            }}
          >
            <div style={{ flex: 1, height: 1, backgroundColor: "rgba(255,255,255,0.15)" }} />
            <Text c="rgba(255,255,255,0.4)" fz={12} px={12} tt="uppercase" fw={600}>
              {locale?.errors?.androidCameraBlockOrLabel || "or"}
            </Text>
            <div style={{ flex: 1, height: 1, backgroundColor: "rgba(255,255,255,0.15)" }} />
          </div>

          <Text c="rgba(255,255,255,0.85)" fz={15} fw={600} align="center" mb={14} maw={340}>
            {locale?.errors?.androidCameraBlockOrFix || "Unblock the browser camera"}
          </Text>
          <div style={{ textAlign: "left", width: "100%", maxWidth: 340, marginBottom: 20 }}>
            <Text c="rgba(255,255,255,0.78)" fz={14} mb={10} lh={1.5}>
              {step1}
            </Text>
            <Text c="rgba(255,255,255,0.78)" fz={14} mb={10} lh={1.5}>
              {step2}
            </Text>
            <Text c="rgba(255,255,255,0.78)" fz={14} mb={10} lh={1.5}>
              {step3}
            </Text>
            <Text c="rgba(255,255,255,0.78)" fz={14} lh={1.5}>
              {step4}
            </Text>
          </div>
        </>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={handleRefresh}
        mt={isGeneric ? 32 : 0}
        styles={{
          root: {
            color: "rgba(255,255,255,0.85)",
            borderColor: "rgba(255,255,255,0.3)",
            borderWidth: 1,
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.06)",
              borderColor: "rgba(255,255,255,0.5)",
            },
          },
        }}
      >
        {refreshButtonLabel}
      </Button>
    </div>
  );

  return ReactDOM.createPortal(portal, document.body);
}
